import React from 'react';
import { Wallet, AlertTriangle, ClipboardList, RefreshCw } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * KpiCards — 4 farbige KPI-Karten fuer das Lager-Dashboard.
 * Props:
 *   kpis        {gesamtbestandswert, kritische_bestaende, offene_banfen, lagerumschlag}
 *   onDrilldown (filter: object) => void   optional: Klick auf Karte => Drill-down in Lagerbestand
 */
const KpiCards = ({ kpis, onDrilldown }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!kpis) return null;

  const { gesamtbestandswert, kritische_bestaende, offene_banfen, lagerumschlag } = kpis;
  const hasDrilldown = typeof onDrilldown === 'function';

  const formatEur = (val) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(val ?? 0);

  // Farbschema je Kennzahl (Akzent für Icon-Chip, Wert, dezenter Karten-Tint)
  const ACCENTS = {
    emerald: {
      iconWrap: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-600',
      value: isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
      tint: isDarkMode ? 'from-emerald-500/10' : 'from-emerald-50',
      ring: isDarkMode ? 'hover:border-emerald-400/40' : 'hover:border-emerald-300',
    },
    rose: {
      iconWrap: isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-600',
      value: isDarkMode ? 'text-rose-300' : 'text-rose-700',
      tint: isDarkMode ? 'from-rose-500/10' : 'from-rose-50',
      ring: isDarkMode ? 'hover:border-rose-400/40' : 'hover:border-rose-300',
    },
    blue: {
      iconWrap: isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600',
      value: isDarkMode ? 'text-blue-200' : 'text-blue-700',
      tint: isDarkMode ? 'from-blue-500/10' : 'from-blue-50',
      ring: isDarkMode ? 'hover:border-blue-400/40' : 'hover:border-blue-300',
    },
    violet: {
      iconWrap: isDarkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600',
      value: isDarkMode ? 'text-violet-200' : 'text-violet-700',
      tint: isDarkMode ? 'from-violet-500/10' : 'from-violet-50',
      ring: isDarkMode ? 'hover:border-violet-400/40' : 'hover:border-violet-300',
    },
  };

  const labelCls = isDarkMode ? 'text-xs text-white/55 font-medium' : 'text-xs text-slate-500 font-medium';
  const unitCls = isDarkMode ? 'text-xs text-white/40' : 'text-xs text-slate-400';
  const hintCls = isDarkMode ? 'text-[10px] text-white/25 mt-1' : 'text-[10px] text-slate-300 mt-1';
  const mockBadge = isDarkMode
    ? 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 ml-2'
    : 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 ml-2';

  const handleKey = (e, filter) => {
    if ((e.key === 'Enter' || e.key === ' ') && hasDrilldown) { e.preventDefault(); onDrilldown(filter); }
  };

  const Card = ({ accentKey, Icon, label, value, unit, isMock, filter, clickable }) => {
    const a = ACCENTS[accentKey];
    const base = isDarkMode
      ? `relative overflow-hidden bg-gradient-to-br ${a.tint} to-transparent bg-white/[0.04] border border-white/12 rounded-2xl p-5`
      : `relative overflow-hidden bg-gradient-to-br ${a.tint} to-white border border-slate-200 rounded-2xl p-5 shadow-sm`;
    const click = clickable
      ? `cursor-pointer transition-all ${a.ring} ${isDarkMode ? 'hover:bg-white/[0.07] focus:outline-none focus:ring-2 focus:ring-brand/40' : 'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand/30'}`
      : '';
    return (
      <div
        className={`${base} ${click}`}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => onDrilldown(filter) : undefined}
        onKeyDown={clickable ? (e) => handleKey(e, filter) : undefined}
        aria-label={clickable ? t('lm.drilldownClickable') : undefined}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* Mock-Badge: Mock-Daten muessen sichtbar als solche markiert sein (HANDOVER.md),
                sonst wirken sie wie Echtdaten. isMock kommt aus source==='mock' des Backends. */}
            <div className={labelCls}>
              {label}
              {isMock && <span className={mockBadge}>{t('lm.mockBadge')}</span>}
            </div>
            <div className={`text-2xl font-bold mt-0.5 ${a.value}`}>{value}</div>
          </div>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.iconWrap}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        <div className={unitCls}>{unit}</div>
        {clickable && <div className={hintCls}>{t('lm.drilldownClickable')}</div>}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Card accentKey="emerald" Icon={Wallet} label={t('lm.dashBestandswert')}
        value={formatEur(gesamtbestandswert?.value)} unit={gesamtbestandswert?.currency || 'EUR'}
        isMock={gesamtbestandswert?.source === 'mock'} filter={{}} clickable={hasDrilldown} />
      <Card accentKey="rose" Icon={AlertTriangle} label={t('lm.dashKritisch')}
        value={kritische_bestaende?.count ?? 0} unit={t('lm.dashKritischUnit')}
        isMock={kritische_bestaende?.source === 'mock'} filter={{ kritisch: true }} clickable={hasDrilldown} />
      <Card accentKey="blue" Icon={ClipboardList} label={t('lm.dashOffeneBanf')}
        value={offene_banfen?.count ?? 0} unit={t('lm.dashOffeneBanfUnit')}
        isMock={offene_banfen?.source === 'mock'} filter={{ offene_banf: 'mit' }} clickable={hasDrilldown} />
      <Card accentKey="violet" Icon={RefreshCw} label={t('lm.dashUmschlag')}
        value={lagerumschlag?.value ?? 0} unit={t('lm.dashUmschlagUnit')}
        isMock={lagerumschlag?.source === 'mock'} filter={{}} clickable={false} />
    </div>
  );
};

export default KpiCards;
